import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  AuthProvider as OidcAuthProvider,
  useAuth as useOidcAuth,
} from 'react-oidc-context'
import { UserManager, WebStorageStateStore } from 'oidc-client-ts'
import type { ReactNode } from 'react'
import type { AccountInfoResponse } from '@/lib/api'
import { ApiError, useCurrentUser } from '@/lib/api'
import { setSessionUser } from '@/lib/observability'

// Type guard to check if an error is an ApiError
function isApiError(error: unknown): error is ApiError {
  return (
    error instanceof ApiError ||
    (error instanceof Error &&
      'status' in error &&
      typeof (error as ApiError).status === 'number')
  )
}

// Get status code from any error type
function getErrorStatus(error: unknown): number | null {
  if (isApiError(error)) {
    return error.status
  }
  return null
}

// Exported interface - maintains compatibility with old useAuth()
export interface AuthContextType {
  user: AccountInfoResponse | null
  isAuthenticated: boolean
  isLoading: boolean
  authError: ApiError | null
  login: () => Promise<void>
  logout: () => void
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Get OIDC configuration
function getOidcSettings() {
  const clientId =
    import.meta.env.VITE_OAUTH_CLIENT_ID ||
    'dyb53pbdYPS7FLEU2rmkL2B1WRVUCfAUrTPoB5jx'

  const redirectUri =
    import.meta.env.VITE_OAUTH_REDIRECT_URI ||
    (typeof window !== 'undefined'
      ? `${window.location.origin}/callback`
      : '/callback')

  const authBaseUrl =
    import.meta.env.VITE_AUTH_BASE_URL || 'https://kuber-auth.aminsaedi.com'

  const postLogoutRedirectUri =
    typeof window !== 'undefined' ? window.location.origin : '/'

  return {
    authority: authBaseUrl,
    metadata: {
      issuer: `${authBaseUrl}/application/o/car-api/`,
      authorization_endpoint: `${authBaseUrl}/application/o/authorize/`,
      token_endpoint: `${authBaseUrl}/application/o/token/`,
      userinfo_endpoint: `${authBaseUrl}/application/o/userinfo/`,
      end_session_endpoint: `${authBaseUrl}/application/o/car-api/end-session/`,
      jwks_uri: `${authBaseUrl}/application/o/car-api/jwks/`,
    },
    client_id: clientId,
    redirect_uri: redirectUri,
    post_logout_redirect_uri: postLogoutRedirectUri,
    scope: 'openid profile email groups offline_access',
    response_type: 'code',
    automaticSilentRenew: true,
    accessTokenExpiringNotificationTimeInSeconds: 60,
    monitorSession: true,
    includeIdTokenInSilentRenew: true,
    loadUserInfo: false,
    silentRequestTimeoutInSeconds: 30,
  }
}

// Create UserManager singleton for use outside React (e.g., API client)
// Only create on client-side
let _userManager: UserManager | null = null

export function getUserManager(): UserManager | null {
  if (typeof window === 'undefined') {
    return null
  }

  if (!_userManager) {
    const settings = getOidcSettings()
    _userManager = new UserManager({
      ...settings,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
    })
  }

  return _userManager
}

// Export userManager for API client middleware
export const userManager = getUserManager()

// Inner component that provides the compatibility layer
function AuthContextProvider({ children }: { children: ReactNode }) {
  const oidcAuth = useOidcAuth()
  const queryClient = useQueryClient()

  // Track if we're attempting to refresh an expired token on startup
  const [isRefreshingExpiredToken, setIsRefreshingExpiredToken] =
    useState(false)
  // Track if startup refresh has been attempted (to avoid infinite loops)
  const [startupRefreshAttempted, setStartupRefreshAttempted] = useState(false)

  // Check if OIDC has a valid session (non-expired token)
  const hasValidOidcSession =
    oidcAuth.isAuthenticated && !!oidcAuth.user?.access_token

  // Check if we have a potentially refreshable session (expired token but has refresh_token)
  // This handles the case when browser is reopened after access token expiry
  const hasExpiredButRefreshableSession =
    !oidcAuth.isAuthenticated &&
    !oidcAuth.isLoading &&
    !!oidcAuth.user?.refresh_token

  // Attempt to refresh expired token on startup
  // This runs once when we detect an expired token with a valid refresh token
  useEffect(() => {
    if (
      hasExpiredButRefreshableSession &&
      !startupRefreshAttempted &&
      !isRefreshingExpiredToken &&
      _userManager
    ) {
      console.log(
        '[AuthOidc] Detected expired token with refresh_token, attempting silent refresh on startup',
      )
      setIsRefreshingExpiredToken(true)
      setStartupRefreshAttempted(true)

      _userManager
        .signinSilent()
        .then(() => {
          console.log('[AuthOidc] Startup token refresh successful')
        })
        .catch((error) => {
          console.error('[AuthOidc] Startup token refresh failed:', error)
          // Clear the invalid session so user can re-login
          _userManager?.removeUser()
        })
        .finally(() => {
          setIsRefreshingExpiredToken(false)
        })
    }
  }, [
    hasExpiredButRefreshableSession,
    startupRefreshAttempted,
    isRefreshingExpiredToken,
  ])

  // Determine if we have an active OIDC session (either valid or being refreshed)
  const hasOidcSession = hasValidOidcSession

  // Use TanStack Query for user data - only fetch if we have OIDC session
  const {
    data: apiUser,
    isLoading: isQueryLoading,
    refetch,
    error,
  } = useCurrentUser({
    enabled: hasOidcSession,
  })

  // Set OpenReplay user identity when user data is available
  useEffect(() => {
    if (apiUser?.email) {
      setSessionUser(apiUser.email, {
        name: apiUser.name || '',
      })
    }
  }, [apiUser])

  // Handle auth errors (clear session on 401)
  useEffect(() => {
    const status = getErrorStatus(error)
    if (status === 401) {
      // Token is invalid and refresh failed - remove user
      oidcAuth.removeUser()
      // Clear query cache to remove stale data
      queryClient.clear()
    }
  }, [error, queryClient, oidcAuth])

  // Handle OIDC events for cross-tab sync
  useEffect(() => {
    if (!_userManager) return

    const handleUserLoaded = () => {
      console.log('[AuthOidc] User loaded (possibly from another tab)')
      // Refetch user data when token is refreshed
      refetch()
    }

    const handleUserUnloaded = () => {
      console.log('[AuthOidc] User unloaded')
      queryClient.clear()
    }

    const handleSilentRenewError = (renewError: Error) => {
      console.error('[AuthOidc] Silent renew error:', renewError)
    }

    // Handle access token expiration - attempt to refresh using refresh token
    // This handles cases where automaticSilentRenew didn't fire (e.g., browser was closed)
    const handleAccessTokenExpired = async () => {
      console.log('[AuthOidc] Access token expired, attempting refresh')
      try {
        const user = await _userManager?.getUser()
        if (user?.refresh_token) {
          await _userManager?.signinSilent()
          console.log('[AuthOidc] Token refresh after expiry successful')
        }
      } catch (err) {
        console.error('[AuthOidc] Token refresh after expiry failed:', err)
      }
    }

    _userManager.events.addUserLoaded(handleUserLoaded)
    _userManager.events.addUserUnloaded(handleUserUnloaded)
    _userManager.events.addSilentRenewError(handleSilentRenewError)
    _userManager.events.addAccessTokenExpired(handleAccessTokenExpired)

    return () => {
      _userManager?.events.removeUserLoaded(handleUserLoaded)
      _userManager?.events.removeUserUnloaded(handleUserUnloaded)
      _userManager?.events.removeSilentRenewError(handleSilentRenewError)
      _userManager?.events.removeAccessTokenExpired(handleAccessTokenExpired)
    }
  }, [refetch, queryClient])

  // Proactively refresh token when app comes to foreground
  // This prevents 401 errors from stale tokens after the app was backgrounded
  useEffect(() => {
    const mgr = _userManager
    if (!mgr) return

    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return

      try {
        const user = await mgr.getUser()
        if (!user) return

        const expiresIn = user.expires_in ?? 0
        const needsRefresh = user.expired || expiresIn < 60

        if (needsRefresh && user.refresh_token) {
          console.log('[AuthOidc] Proactive token refresh on resume')
          await mgr.signinSilent()
          refetch()
        }
      } catch (refreshErr) {
        console.error('[AuthOidc] Proactive refresh failed:', refreshErr)
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [refetch])

  const login = useCallback(async () => {
    await oidcAuth.signinRedirect()
  }, [oidcAuth])

  const logout = useCallback(() => {
    // Clear all TanStack Query cache on logout
    queryClient.clear()
    oidcAuth.signoutRedirect()
  }, [queryClient, oidcAuth])

  const refreshUser = useCallback(async () => {
    await refetch()
  }, [refetch])

  // User is valid if they have at least one accessible account
  const validUser =
    apiUser &&
    apiUser.accessible_accounts &&
    apiUser.accessible_accounts.length > 0
      ? apiUser
      : null

  // Determine if there's an auth error that requires re-login
  const authError = isApiError(error) && error.status === 401 ? error : null

  // Calculate loading state
  // Include isRefreshingExpiredToken AND hasExpiredButRefreshableSession to prevent
  // route-level auth guards from triggering login() redirect before our startup
  // refresh effect has a chance to run. Effects execute after render, so without
  // this, there's a single render frame where isLoading=false and isAuthenticated=false
  // which causes an immediate redirect to the auth provider.
  const isLoading =
    oidcAuth.isLoading ||
    isRefreshingExpiredToken ||
    hasExpiredButRefreshableSession ||
    (hasOidcSession && isQueryLoading)

  const value = useMemo<AuthContextType>(
    () => ({
      user: validUser,
      isAuthenticated: !!validUser,
      isLoading,
      authError,
      login,
      logout,
      refreshUser,
    }),
    [validUser, isLoading, authError, login, logout, refreshUser],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// Main AuthProvider that wraps OIDC provider and our compatibility layer
export function AuthProvider({ children }: { children: ReactNode }) {
  // SSR guard - render children without auth context on server
  if (typeof window === 'undefined') {
    return (
      <AuthContext.Provider
        value={{
          user: null,
          isAuthenticated: false,
          isLoading: true,
          authError: null,
          login: async () => {},
          logout: () => {},
          refreshUser: async () => {},
        }}
      >
        {children}
      </AuthContext.Provider>
    )
  }

  // Use the singleton UserManager so that react-oidc-context and our own code
  // (startup refresh, event handlers, API client) all share the same instance.
  // Without this, signinSilent() on our singleton wouldn't update oidcAuth state.
  const um = getUserManager()!

  // Callback to handle sign-in completion - navigation is handled by callback route
  const onSigninCallback = () => {
    // Clear URL params after successful signin
    window.history.replaceState({}, document.title, window.location.pathname)
  }

  return (
    <OidcAuthProvider userManager={um} onSigninCallback={onSigninCallback}>
      <AuthContextProvider>{children}</AuthContextProvider>
    </OidcAuthProvider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
